import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { declarationSchema, licenseTierShort, type DeclarationFormData, type RegionalLicense } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import CultivarPicker, { type PickedCultivar } from '../orchard/CultivarPicker';
import { FileText, Trees, Weight, Package, Truck, Loader2 } from 'lucide-react';
import PbrMark from './PbrMark';
import PbrNotice from './PbrNotice';

interface DeclarationFormProps {
  licenseId?: number;
  initialData?: Partial<DeclarationFormData>;
  onSubmit: (data: DeclarationFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

function DeclarationForm({
  licenseId,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false
}: DeclarationFormProps) {
  const [license, setLicense] = useState<RegionalLicense | null>(null);
  const [loadingLicense, setLoadingLicense] = useState(!!licenseId);
  const [packFormats, setPackFormats] = useState<{ format: string; count: number }[]>(
    initialData?.pack_formats || []
  );
  const [markets, setMarkets] = useState<{ market: string; tonnes: number }[]>(
    initialData?.markets || []
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DeclarationFormData>({
    resolver: zodResolver(declarationSchema),
    defaultValues: {
      license_id: licenseId,
      season_year: initialData?.season_year || new Date().getFullYear(),
      trees_planted: initialData?.trees_planted,
      hectares_planted: initialData?.hectares_planted,
      rootstock: initialData?.rootstock || '',
      rootstock_cultivar_id: initialData?.rootstock_cultivar_id ?? null,
      planting_date: initialData?.planting_date || '',
      estimated_production_tonnes: initialData?.estimated_production_tonnes,
      actual_production_tonnes: initialData?.actual_production_tonnes,
      packed_tonnes: initialData?.packed_tonnes,
      marketed_tonnes: initialData?.marketed_tonnes,
      notes: initialData?.notes || '',
    },
  });

  useEffect(() => {
    if (licenseId) {
      loadLicense(licenseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  // Keep the free-text `rootstock` in step with the registry choice so
  // existing readers and older declarations stay comparable.
  const handleRootstockChange = (c: PickedCultivar | null) => {
    setValue('rootstock_cultivar_id', c ? c.id : null);
    setValue('rootstock', c ? c.name : '');
  };

  useEffect(() => {
    setValue('pack_formats', packFormats);
  }, [packFormats, setValue]);

  useEffect(() => {
    setValue('markets', markets);
  }, [markets, setValue]);

  const loadLicense = async (id: number) => {
    setLoadingLicense(true);
    try {
      const response = await cultivarService.getLicense(id);
      if (response.success && response.data) {
        setLicense(response.data);
        setValue('license_id', id);
        // Declarations are made against a cultivar licence; a master has none.
        if (response.data.cultivar_id != null) setValue('cultivar_id', response.data.cultivar_id);
      }
    } catch (err) {
      console.error('Error loading license:', err);
    } finally {
      setLoadingLicense(false);
    }
  };

  const addPackFormat = () => {
    setPackFormats([...packFormats, { format: '', count: 0 }]);
  };

  const updatePackFormat = (index: number, field: 'format' | 'count', value: string | number) => {
    const updated = [...packFormats];
    updated[index] = { ...updated[index], [field]: value };
    setPackFormats(updated);
  };

  const removePackFormat = (index: number) => {
    setPackFormats(packFormats.filter((_, i) => i !== index));
  };

  const addMarket = () => {
    setMarkets([...markets, { market: '', tonnes: 0 }]);
  };

  const updateMarket = (index: number, field: 'market' | 'tonnes', value: string | number) => {
    const updated = [...markets];
    updated[index] = { ...updated[index], [field]: value };
    setMarkets(updated);
  };

  const removeMarket = (index: number) => {
    setMarkets(markets.filter((_, i) => i !== index));
  };

  if (loadingLicense) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* PBR notice — must accompany the symbol shown beside the cultivar name above. */}
      <PbrNotice />
      {/* License Info */}
      {license && (
        <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary-600" />
            <div>
              <p className="font-medium text-primary-900">
                {license.cultivar_name}
                {/* Licences are only issued for PBR-protected varieties — force the mark. */}
                <PbrMark status="protected" />
                {' - '}
                {licenseTierShort(license.license_tier)} Licence
              </p>
              <p className="text-sm text-primary-700">
                From {license.licensor_org_name}
                {license.region && ` • ${license.region}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Season */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Input
          label="Season Year *"
          type="number"
          {...register('season_year', { valueAsNumber: true })}
          error={errors.season_year?.message}
          min={2000}
          max={2100}
        />
        <CultivarPicker
          label="Rootstock"
          placeholder="e.g., M9, MM106"
          cultivarRole="rootstock"
          value={watch('rootstock_cultivar_id')}
          initialDisplayName={initialData?.rootstock || ''}
          onChange={handleRootstockChange}
          error={errors.rootstock_cultivar_id?.message}
        />
        <Input
          label="Planting Date"
          type="date"
          {...register('planting_date')}
          error={errors.planting_date?.message}
        />
      </div>

      {/* Planting Data */}
      <div className="border border-neutral-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Trees className="h-5 w-5 text-success-600" />
          Planting Data
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Trees Planted"
            type="number"
            {...register('trees_planted', { valueAsNumber: true })}
            error={errors.trees_planted?.message}
            placeholder="Number of trees"
          />
          <Input
            label="Hectares Planted"
            type="number"
            step="0.01"
            {...register('hectares_planted', { valueAsNumber: true })}
            error={errors.hectares_planted?.message}
            placeholder="Area in hectares"
          />
        </div>
      </div>

      {/* Production Data */}
      <div className="border border-neutral-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Weight className="h-5 w-5 text-warning-600" />
          Production Volumes (Tonnes)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Estimated Production"
            type="number"
            step="0.01"
            {...register('estimated_production_tonnes', { valueAsNumber: true })}
            error={errors.estimated_production_tonnes?.message}
            placeholder="Expected tonnes"
          />
          <Input
            label="Actual Production"
            type="number"
            step="0.01"
            {...register('actual_production_tonnes', { valueAsNumber: true })}
            error={errors.actual_production_tonnes?.message}
            placeholder="Actual tonnes harvested"
          />
        </div>
      </div>

      {/* Packing Data — relevant on orchard_planting licences */}
      {license?.license_tier === 'orchard_planting' && (
        <div className="border border-neutral-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-purple-600" />
            Packing Data
          </h3>
          <Input
            label="Total Packed (Tonnes)"
            type="number"
            step="0.01"
            {...register('packed_tonnes', { valueAsNumber: true })}
            error={errors.packed_tonnes?.message}
            className="mb-4"
          />

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-neutral-700">Pack Formats</label>
            {packFormats.map((pf, index) => (
              <div key={index} className="flex items-center gap-3">
                <Input
                  placeholder="Format (e.g., carton_18kg)"
                  value={pf.format}
                  onChange={(e) => updatePackFormat(index, 'format', e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  placeholder="Count"
                  value={pf.count || ''}
                  onChange={(e) => updatePackFormat(index, 'count', Number(e.target.value))}
                  className="w-32"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePackFormat(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addPackFormat}>
              Add Pack Format
            </Button>
          </div>
        </div>
      )}

      {/* Marketing Data — relevant on orchard_planting licences */}
      {license?.license_tier === 'orchard_planting' && (
        <div className="border border-neutral-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Truck className="h-5 w-5 text-orange-600" />
            Marketing Data
          </h3>
          <Input
            label="Total Marketed (Tonnes)"
            type="number"
            step="0.01"
            {...register('marketed_tonnes', { valueAsNumber: true })}
            error={errors.marketed_tonnes?.message}
            className="mb-4"
          />

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-neutral-700">Markets</label>
            {markets.map((m, index) => (
              <div key={index} className="flex items-center gap-3">
                <Input
                  placeholder="Market (e.g., domestic, export_asia)"
                  value={m.market}
                  onChange={(e) => updateMarket(index, 'market', e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Tonnes"
                  value={m.tonnes || ''}
                  onChange={(e) => updateMarket(index, 'tonnes', Number(e.target.value))}
                  className="w-32"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMarket(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addMarket}>
              Add Market
            </Button>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Notes</label>
        <textarea
          {...register('notes')}
          className="input-professional w-full min-h-[100px]"
          placeholder="Any additional notes about this declaration..."
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-200">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          Submit Declaration
        </Button>
      </div>
    </form>
  );
}

export default DeclarationForm;
